import { FormControl, FormHelperText, FormLabel } from '@mui/joy';

import { ReactNode, useState } from 'react';

import { validateBackfillDate } from '@/Pages/Settings/utils/helpers';
import { Input } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { RuleSchema, SessionRuleMutateBody } from '@/types/schema';

import {
  BackfillDateInput,
  BackfillFromFormLabel,
  RulesFilterInput,
  RulesSamplingRateSlider,
} from '../Rules/SessionRulesCrudFormV2';
import { SearchModel } from '../RunsTable/types';
import { Switch } from '../Switch/Switch';
import { useEvaluatorStore } from './store/evaluatorStore';

export interface SessionRuleFormProps {
  initialData?: RuleSchema;
  sessionId: string;
  onFormChange: (form: {
    data: Partial<SessionRuleMutateBody>;
    isValid: boolean;
  }) => void;
  filters: SearchModel;
  setFilters: (filters: SearchModel) => void;
  isCodeEvaluator?: boolean;
  filterLabel?: string;
  renderFilters?: (args: {
    filters: SearchModel;
    setFilters: (filters: SearchModel) => void;
  }) => ReactNode;
}

interface FormState {
  displayName: string;
  samplingRate: number;
  backfillFrom: string | undefined;
  includeExtendedStats: boolean;
}

export function SessionRuleForm(props: SessionRuleFormProps) {
  const {
    initialData,
    sessionId,
    onFormChange,
    filters,
    setFilters,
    filterLabel,
    renderFilters,
  } = props;
  const { value: showExtendedStatsFlag } = useOrgConfig(
    OrgConfigs.enable_include_extended_stats
  );
  const setBackfillFrom = useEvaluatorStore((state) => state.setBackfillFrom);

  const [formState, setFormState] = useState<FormState>({
    displayName: initialData?.display_name ?? '',
    samplingRate: initialData?.sampling_rate ?? 1,
    backfillFrom: initialData?.backfill_from,
    includeExtendedStats: initialData?.include_extended_stats ?? false,
  });

  const [backfillFromError, setBackfillFromError] = useState<string>();

  const [applyToPastRuns, _setApplyToPastRuns] = useState(false);
  const [isNameInvalid, setIsNameInvalid] = useState(false);

  const setApplyToPastRuns = (updater: React.SetStateAction<boolean>) => {
    _setApplyToPastRuns((prev) => {
      const mApplyToPastRuns =
        typeof updater === 'function' ? updater(prev) : updater;
      if (!mApplyToPastRuns) {
        setBackfillFrom(null);
        setBackfillFromError(undefined);
      }
      return mApplyToPastRuns;
    });
  };

  const updateFormState = (updates: Partial<FormState>) => {
    const newState = { ...formState, ...updates };
    setFormState(newState);

    const isValid = !!newState.displayName;
    setIsNameInvalid(!isValid);

    if (newState.backfillFrom) {
      const validationResult = validateBackfillDate(newState.backfillFrom);
      if (!validationResult) {
        setBackfillFromError(validationResult);
      } else {
        setBackfillFromError(undefined);
      }
    }

    setBackfillFrom(newState.backfillFrom || null);

    onFormChange({
      data: {
        ...initialData,
        display_name: newState.displayName,
        sampling_rate: newState.samplingRate,
        backfill_from: newState.backfillFrom,
        include_extended_stats: newState.includeExtendedStats,
      },
      isValid,
    });
  };

  const hasBackfillFrom = !!props.initialData?.backfill_from;

  return (
    <div className="flex flex-col gap-4 pl-4">
      <FormControl error={isNameInvalid}>
        <FormLabel>Name</FormLabel>
        <Input
          type="text"
          placeholder="Name"
          value={formState.displayName}
          onChange={(value) => {
            updateFormState({ displayName: value });
          }}
        />
        <FormHelperText className="empty:hidden">
          {isNameInvalid && 'Name is required'}
        </FormHelperText>
      </FormControl>
      <div className="flex gap-6">
        <FormControl>
          <FormLabel>{filterLabel ?? 'Run Filters'}</FormLabel>
          <div>
            {renderFilters ? (
              renderFilters({ filters, setFilters })
            ) : (
              <RulesFilterInput
                sessionId={sessionId}
                value={filters}
                onChange={setFilters}
              />
            )}
          </div>
        </FormControl>

        <FormControl sx={{ width: '185px' }}>
          <FormLabel>Sampling Rate</FormLabel>
          <RulesSamplingRateSlider
            value={formState.samplingRate}
            onChange={(value) => {
              updateFormState({ samplingRate: value });
            }}
          />
        </FormControl>
      </div>

      {props.isCodeEvaluator && showExtendedStatsFlag && (
        <div className="flex flex-row gap-2">
          <Switch
            label="Include extended stats (feedback, costs, tokens)"
            checked={formState.includeExtendedStats}
            onChange={(checked) => {
              updateFormState({ includeExtendedStats: checked });
            }}
          />
        </div>
      )}

      {(!props.initialData || hasBackfillFrom) && (
        <div className="flex min-h-[40px] flex-row gap-6">
          {!props.initialData && (
            <Switch
              label="Apply to past runs"
              checked={applyToPastRuns}
              onChange={(checked) => setApplyToPastRuns(checked)}
            />
          )}
          {(applyToPastRuns || hasBackfillFrom) && (
            <FormControl
              error={!!backfillFromError}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <BackfillFromFormLabel disabled={!!props.initialData} />

              <BackfillDateInput
                value={formState.backfillFrom}
                onChange={(value) => {
                  updateFormState({ backfillFrom: value ?? undefined });
                }}
                disabled={!!props.initialData}
              />
            </FormControl>
          )}
        </div>
      )}
    </div>
  );
}
